/**
 * useCorpusChipResolver — returns a memoized `(label) => CorpusChipData | null`
 * that maps labels in streamed AI replies back to their Corpus document /
 * folder / bucket, so ChatMarkdown can render them as pills.
 *
 * As a side-effect the hook also pushes an id-indexed snapshot of the same
 * docs/folders/buckets into the shared workspace chip index in
 * `@/lib/refMarkers`, so the Draft Canvas can resolve `[ref:kind:id]`
 * markers emitted by the Writing Agent even when the id wasn't pinned at
 * intake.
 *
 * Longest-match wins so multi-word titles win over shorter prefixes. Matching
 * is case-insensitive; labels are compared with collapsed whitespace.
 */
import { useEffect, useMemo, useState } from "react";
import { listDocuments, listFolders, BUCKET_LABELS, BUCKET_ORDER } from "@/lib/workspaceCorpus";
import { setWorkspaceChipIndex, type WorkspaceChip } from "@/lib/refMarkers";
import type { CorpusChipData } from "./CorpusChipPill";

const norm = (s: string) =>
  s
    .normalize("NFC")
    // fold smart quotes / dashes to ascii
    .replace(/[\u2018\u2019\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201F\u2033]/g, '"')
    .replace(/[\u2013\u2014\u2212]/g, "-")
    // ampersand <-> "and"
    .replace(/\s*&\s*/g, " and ")
    // strip everything that isn't a letter/number/space
    .replace(/[^\p{L}\p{N}\s]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const normAliases = (s: string): string[] => {
  const exact = s.normalize("NFC").replace(/\s+/g, " ").trim().toLowerCase();
  return Array.from(new Set([exact, norm(s)].filter(Boolean)));
};

export type ChipResolver = ((label: string) => CorpusChipData | null) & {
  /** Candidate labels safe for assistant-reply plain-text matching. */
  plainTextChips?: CorpusChipData[];
  /** Resolve a `[ref:kind:id]` marker (kind ∈ corpus|doc|folder|bucket|…). */
  byRefMarker?: (kind: string, id: string) => CorpusChipData | null;
};

export function useCorpusChipResolver(workspaceId: string | undefined): ChipResolver {
  const [docs, setDocs] = useState<Array<{ id: string; title: string }>>([]);
  const [folders, setFolders] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      try {
        const [d, f] = await Promise.all([
          listDocuments(workspaceId),
          listFolders(workspaceId),
        ]);
        if (cancelled) return;
        setDocs(d.map((x) => ({ id: x.id, title: x.title })));
        setFolders(f.map((x) => ({ id: x.id, name: x.name })));
      } catch {
        /* noop — resolver falls back to plain text */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  // Push an id-indexed snapshot into the shared workspace chip index so the
  // Draft Canvas can render document chips for markers coming from grounding
  // retrieval (not just pinned intake).
  useEffect(() => {
    const chips: WorkspaceChip[] = [];
    for (const b of BUCKET_ORDER) {
      chips.push({ kind: "bucket", id: b, label: BUCKET_LABELS[b] });
    }
    for (const f of folders) chips.push({ kind: "folder", id: f.id, label: f.name });
    for (const d of docs) chips.push({ kind: "doc", id: d.id, label: d.title });
    setWorkspaceChipIndex(chips);
  }, [docs, folders]);

  return useMemo<ChipResolver>(() => {
    const map = new Map<string, CorpusChipData>();
    const addAliases = (label: string, chip: CorpusChipData) => {
      for (const key of normAliases(label)) {
        if (key && !map.has(key)) map.set(key, chip);
      }
    };
    for (const b of BUCKET_ORDER) {
      addAliases(BUCKET_LABELS[b], { kind: "bucket", id: b, label: BUCKET_LABELS[b] });
    }
    for (const f of folders) {
      addAliases(f.name, { kind: "folder", id: f.id, label: f.name });
    }
    for (const d of docs) {
      addAliases(d.title, { kind: "doc", id: d.id, label: d.title });
    }
    const resolve = ((label: string) => map.get(norm(label)) ?? null) as ChipResolver;
    resolve.plainTextChips = [
      ...folders.map((f) => ({ kind: "folder" as const, id: f.id, label: f.name })),
      ...docs.map((d) => ({ kind: "doc" as const, id: d.id, label: d.title })),
    ]
      .filter((chip) => norm(chip.label).length >= 4)
      .sort((a, b) => b.label.length - a.label.length);

    const docById = new Map(docs.map((d) => [d.id, d]));
    const folderById = new Map(folders.map((f) => [f.id, f]));
    resolve.byRefMarker = (kind: string, id: string): CorpusChipData | null => {
      const k = kind.toLowerCase();
      if (k === "corpus" || k === "doc" || k === "document") {
        const d = docById.get(id);
        if (d) return { kind: "doc", id: d.id, label: d.title };
        return null;
      }
      if (k === "folder") {
        const f = folderById.get(id);
        if (f) return { kind: "folder", id: f.id, label: f.name };
        return null;
      }
      if (k === "bucket") {
        const label = (BUCKET_LABELS as Record<string, string>)[id];
        if (label) return { kind: "bucket", id, label };
        return null;
      }
      return null;
    };
    return resolve;
  }, [docs, folders]);
}
